import { ENTERPRISE } from 'self-service-api/constants/ProductLevels';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { useAppSelector } from '../../../reducers';
import { commonAvailableTermsSelector } from '../../../selectors/OfferSelectors';
import { checkoutProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { crmSuiteProductLevelPreSeatsAndSeatsOnlySelector, currentSkuIdForProductSelector, skuIdsContainOnlyCrmSuiteSelector } from '../../../selectors/ProductsSelectors';
import { commonPurchaseMotionsSelector, purchaseMotionsSelector, suitePurchaseMotionsMapSelector, talkToSalesTypeSelector } from '../../../selectors/PurchaseMotionSelectors';

/**
 * Hook to determine which purchase motions to display for HubSpot Credits tiles.
 *
 * USE WITH CAUTION:
 * - This hook is used to determine which purchase motions to display for HubSpot Credits tiles.
 * - It should only be used in the HubSpot Credits tile component.
 *
 * @param unlockHubSpotCreditsProductConfigurations - Product configurations that unlock HubSpot Credits
 * @param creditsLocation - Location of the credits
 * @returns Purchase motions to display
 */
export const usePurchaseMotionsForHubSpotCreditsTile = ({
  unlockHubSpotCreditsProductConfigurations = [],
  creditsLocation
}) => {
  const talkToSalesType = useAppSelector(talkToSalesTypeSelector);

  // PURCHASE MOTIONS FOR CREDITS
  const creditsSkuId = useAppSelector(state => currentSkuIdForProductSelector(state, {
    apiName: 'hubspot-credits-committed'
  }));
  const creditsPurchaseMotions = useAppSelector(state => {
    if (!creditsSkuId) {
      return [];
    }
    return purchaseMotionsSelector(state, {
      location: creditsLocation,
      skuId: creditsSkuId
    });
  });

  // PURCHASE MOTIONS FOR UNLOCK PRODUCTS
  const unlockHubSpotCreditsCheckoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations: unlockHubSpotCreditsProductConfigurations
  }));
  const unlockSkuIds = unlockHubSpotCreditsProductConfigurations.map(({
    skuId
  }) => skuId);
  const suiteProductLevel = useAppSelector(state => crmSuiteProductLevelPreSeatsAndSeatsOnlySelector(state, {
    skuIds: unlockSkuIds
  }));
  const hasOnlySuiteSkuIds = useAppSelector(state => skuIdsContainOnlyCrmSuiteSelector(state, {
    skuIds: unlockSkuIds
  }));
  const unlockPurchaseMotions = useAppSelector(state => {
    // Handle suite products with their special purchase motion map
    if (suiteProductLevel && hasOnlySuiteSkuIds && suiteProductLevel !== ENTERPRISE) {
      const suitePurchaseMotionsMap = suitePurchaseMotionsMapSelector(state);
      if (suitePurchaseMotionsMap && suitePurchaseMotionsMap[suiteProductLevel]) {
        return suitePurchaseMotionsMap[suiteProductLevel];
      }
      return [];
    }

    // For non-suite products
    const skuIdsToUse = unlockHubSpotCreditsCheckoutProductConfigurations.map(({
      skuId
    }) => skuId);
    const commonPurchaseMotions = commonPurchaseMotionsSelector(state, {
      skuIds: skuIdsToUse,
      location: 'MERCH_CARD'
    });
    const commonTerms = commonAvailableTermsSelector(state, {
      skuIds: skuIdsToUse
    });
    const hasCommonTerms = !!commonTerms.length;
    if (!hasCommonTerms) {
      const filteredPurchaseMotions = commonPurchaseMotions.filter(purchaseMotion => purchaseMotion !== BUY_TOUCHLESS);
      if (!filteredPurchaseMotions.length) {
        return [talkToSalesType];
      }
      return filteredPurchaseMotions;
    }
    return commonPurchaseMotions;
  });

  // COMBINED EVALUATION: intersection of unlock and credits purchase motions
  if (unlockHubSpotCreditsProductConfigurations.length === 0) {
    return creditsPurchaseMotions;
  }
  const combinedPurchaseMotions = unlockPurchaseMotions.filter(motion => creditsPurchaseMotions.includes(motion));
  return combinedPurchaseMotions.length > 0 ? combinedPurchaseMotions : [talkToSalesType];
};